import { type ReactNode } from "react"
import { ArrowUpDown } from "lucide-react"
import { cn } from "../../lib/cn"
import { EmptyState } from "./EmptyState"
import { Spinner } from "./Spinner"

export interface DataTableColumn<T> {
  key: string
  header: string
  render: (row: T) => ReactNode
  /** Text alignment for the column; defaults to the start (right in RTL). */
  align?: "start" | "center" | "end"
  sortable?: boolean
  className?: string
  headerClassName?: string
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  rows: T[]
  rowKey: (row: T) => string
  loading?: boolean
  emptyTitle?: string
  emptyDescription?: string
  sortKey?: string
  sortDirection?: "asc" | "desc"
  onSort?: (key: string) => void
  /** When set, rows become clickable (e.g. to open the item on the live site). */
  onRowClick?: (row: T) => void
  className?: string
}

const alignClasses: Record<NonNullable<DataTableColumn<unknown>["align"]>, string> = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  emptyTitle = "لا توجد بيانات",
  emptyDescription,
  sortKey,
  sortDirection,
  onSort,
  onRowClick,
  className,
}: DataTableProps<T>) {
  const isEmpty = !loading && rows.length === 0

  return (
    <div className={cn("overflow-hidden rounded-card border border-border-subtle bg-surface", className)}>
      <div className="max-h-[70vh] overflow-auto">
        <table className="w-full min-w-[640px] border-collapse text-body-sm">
          <thead className="sticky top-0 z-10 bg-surface-muted">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    "border-b border-border-subtle px-lg py-sm text-label font-semibold text-text-secondary",
                    alignClasses[col.align ?? "start"],
                    col.headerClassName,
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort?.(col.key)}
                      className={cn(
                        "inline-flex items-center gap-2xs hover:text-text-primary",
                        sortKey === col.key && "text-accent",
                      )}
                    >
                      {col.header}
                      <ArrowUpDown className="size-3.5" />
                      {sortKey === col.key && (
                        <span className="sr-only">{sortDirection === "asc" ? "تصاعدي" : "تنازلي"}</span>
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  "border-b border-border-subtle last:border-b-0 hover:bg-surface-muted/60",
                  onRowClick && "cursor-pointer",
                )}
              >
                {columns.map((col) => (
                  <td key={col.key} className={cn("px-lg py-sm text-text-primary", alignClasses[col.align ?? "start"], col.className)}>
                    {col.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {loading && rows.length === 0 && (
          <div className="flex items-center justify-center py-3xl">
            <Spinner />
          </div>
        )}

        {isEmpty && <EmptyState title={emptyTitle} description={emptyDescription} />}
      </div>
    </div>
  )
}
